/**
 * 학생 담당 강사 변경 모달
 * - 현재 담당 강사 표시
 * - 새 담당 강사 선택
 * - 할당 안함 옵션
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Typography, Space, message, Spin, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import type { User } from '../../types/user';

const { Text } = Typography;
const { Option } = Select;

interface ChangeInstructorModalProps {
  open: boolean;
  student: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * ChangeInstructorModal 컴포넌트
 */
export const ChangeInstructorModal: React.FC<ChangeInstructorModalProps> = ({
  open,
  student,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState<User[]>([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  /**
   * 강사 목록 조회
   */
  useEffect(() => {
    if (!open) return;

    const fetchInstructors = async () => {
      setLoadingInstructors(true);
      try {
        const response = await userService.getUsers({
          role: 'instructor',
          page_size: 100,
        });
        setInstructors(response.users);
      } catch (error) {
        console.error('강사 목록 조회 실패:', error);
        message.error('강사 목록 조회 실패');
      } finally {
        setLoadingInstructors(false);
      }
    };

    fetchInstructors();
  }, [open]);

  /**
   * 모달 열릴 때 현재 담당 강사로 초기화 (첫 번째 강사)
   */
  useEffect(() => {
    if (open && student) {
      form.setFieldsValue({
        instructor_id: student.instructor_ids?.[0] || undefined,
      });
    }
  }, [open, student, form]);

  /**
   * 담당 강사 변경 처리
   */
  const handleSubmit = async (values: { instructor_id?: string }) => {
    if (!student) return;

    setLoading(true);
    try {
      // 1. 기존 할당 해제 (모든 담당 강사)
      if (student.instructor_ids && student.instructor_ids.length > 0) {
        for (const instructorId of student.instructor_ids) {
          await userService.unassignStudentsFromInstructor({
            instructor_id: instructorId,
            student_ids: [student.id],
          });
        }
      }

      // 2. 새 강사에게 할당 (선택한 경우)
      if (values.instructor_id) {
        await userService.assignStudentsToInstructor({
          instructor_id: values.instructor_id,
          student_ids: [student.id],
        });

        const newInstructor = instructors.find(i => i.id === values.instructor_id);
        message.success(`${student.name} 학생을 ${newInstructor?.name} 강사에게 할당했습니다`);
      } else {
        message.success(`${student.name} 학생의 담당 강사 할당을 해제했습니다`);
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('담당 강사 변경 실패:', error);
      message.error(error.response?.data?.detail || '담당 강사 변경에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 모달 닫기
   */
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="담당 강사 변경"
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="변경"
      cancelText="취소"
    >
      {student && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* 학생 정보 */}
          <div>
            <Text strong>학생:</Text> {student.name} ({student.email})
          </div>

          {/* 현재 담당 강사 */}
          <div>
            <Text strong>현재 담당 강사:</Text>{' '}
            {student.instructor_names && student.instructor_names.length > 0 ? (
              <Space size={4} wrap>
                {student.instructor_names.map((name, index) => (
                  <Tag key={index} color="blue">{name}</Tag>
                ))}
              </Space>
            ) : (
              <Text type="warning">할당 안됨</Text>
            )}
          </div>

          {/* 새 담당 강사 선택 */}
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="instructor_id"
              label="새 담당 강사"
              rules={[]}
            >
              <Select
                placeholder="강사를 선택하세요"
                loading={loadingInstructors}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {instructors.map((instructor) => (
                  <Option key={instructor.id} value={instructor.id}>
                    <Space>
                      <UserOutlined />
                      {instructor.name} ({instructor.email})
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              * 강사를 선택하지 않으면 할당이 해제됩니다
            </Text>
          </Form>
        </Space>
      )}

      {loadingInstructors && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export default ChangeInstructorModal;
